// Pure display helpers for the Fast path quality page (no React).

import type { EvalRunRead } from '../../services/fastpathQuality';

export function whenUtc(iso: string | null | undefined): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 16)} UTC`;
}

export function usd(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '–';
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return '–';
  return `$${n.toFixed(4)}`;
}

export function ms(value: number | null | undefined): string {
  if (value === null || value === undefined) return '–';
  return `${value.toLocaleString()} ms`;
}

/** "model · classifier · prompt" plus any truthy flags — the one-line variant summary. */
export function variantSummary(variant: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const key of ['model', 'classifier_model', 'prompt_variant']) {
    const v = variant[key];
    if (typeof v === 'string' && v) parts.push(v);
  }
  const flags = variant.flags;
  if (flags && typeof flags === 'object') {
    const on = Object.entries(flags as Record<string, unknown>)
      .filter(([, v]) => v === true)
      .map(([k]) => k);
    if (on.length) parts.push(`flags: ${on.join(', ')}`);
  }
  return parts.length ? parts.join(' · ') : '(defaults)';
}

/** correct / total as a percentage; "–" for an empty run. */
export function passRate(run: EvalRunRead): string {
  if (!run.cases_total) return '–';
  return `${Math.round((run.cases_passed / run.cases_total) * 100)}%`;
}

export function triggerLabel(trigger: EvalRunRead['trigger']): string {
  switch (trigger) {
    case 'beat':
      return 'weekly beat';
    case 'api':
      return 'run now';
    default:
      return 'manual (CLI)';
  }
}

/** Short string for a case's grounding issues; empty when the verifier found none. */
export function groundingIssues(
  g: { ungrounded?: string[]; ungrounded_numbers?: string[]; action_claims?: string[] } | null,
): string {
  if (!g) return '';
  const bits: string[] = [];
  const numbers = g.ungrounded ?? g.ungrounded_numbers ?? [];
  if (numbers.length) bits.push(`ungrounded numbers: ${numbers.join(', ')}`);
  if (g.action_claims?.length) bits.push(`action claims: ${g.action_claims.join('; ')}`);
  return bits.join(' · ');
}

/** Clip prose for a table cell; the expanded detail row shows the whole text. */
export function clip(text: string | null | undefined, max = 240): string {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
}

interface RoutingDetail {
  ineligible?: string;
  error?: string | null;
  matched?: boolean;
  entries?: string[];
  candidates?: { id: string; head: string }[];
  classifier_reply?: string;
}

/** One line for the table: the judge's note, how many truth rows, and how routing went. */
export function expectedSummary(expected: Record<string, unknown>): string {
  const bits: string[] = [];
  const note = typeof expected.note === 'string' ? expected.note : '';
  if (note) bits.push(clip(note, 160));
  const rows = Array.isArray(expected.rows) ? expected.rows : null;
  if (rows) bits.push(`${rows.length} truth row${rows.length === 1 ? '' : 's'}${typeof expected.engine === 'string' ? ` (${expected.engine})` : ''}`);
  const routing = expected.routing as RoutingDetail | undefined;
  if (routing) {
    if (routing.ineligible) bits.push(routing.ineligible);
    else if (routing.error) bits.push(`routing: ${routing.error}`);
    else if (routing.matched === false) bits.push(`routing: classifier said not covered (${routing.candidates?.length ?? 0} candidates)`);
    else if (routing.entries?.length) bits.push(`routed on ${routing.entries.map((e) => e.slice(0, 8)).join(', ')}`);
  }
  if (!bits.length) {
    const entries = Object.entries(expected);
    if (!entries.length) return '–';
    return clip(entries.map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`).join(' · '), 200);
  }
  return bits.join(' · ');
}

/** The full truth rows and routing audit for the expanded detail row. */
export function expectedDetail(expected: Record<string, unknown>): { rows: string | null; routing: string | null } {
  const rows = Array.isArray(expected.rows) && expected.rows.length ? JSON.stringify(expected.rows, null, 1) : null;
  const routing = expected.routing ? JSON.stringify(expected.routing, null, 1) : null;
  return { rows, routing };
}
